import React, { useCallback, useMemo } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { IngredientCardV2 } from '@sg/garnish';
import { type IngredientModification } from '@sg/graphql-schema';

import { IngredientModificationsTypes } from '@order/utils';

import { INGREDIENT_IMAGE_WIDTH } from '../../../../constants';

// ─────────────────────────────────────────────────────────────────────────────

export const ProductDetailsIngredientCard = (
  props: ProductDetailsIngredientCardProps,
) => {
  const {
    dietaryRestrictionNote,
    ingredientModification,
    quantity,
    activeStepperId,
    isAtMaxQuantity,
    onIncrement,
    onDecrement,
    setActiveStepperId,
    resetStepperTimeout,
  } = props;

  const isDressing =
    ingredientModification.kind === IngredientModificationsTypes.Dressings;

  const { formatMessage } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const accessibilityLabel = formatMessage(messages.a11yCardLabel, {
    quantity,
    ingredient_name: ingredientModification.ingredient.name,
  });

  // ─────────────────────────────────────────────────────────────────────

  const handleIncrement = useCallback(() => {
    onIncrement(ingredientModification);
    resetStepperTimeout();
  }, [ingredientModification, onIncrement, resetStepperTimeout]);

  const handleDecrement = useCallback(() => {
    onDecrement(ingredientModification);
    resetStepperTimeout();
  }, [ingredientModification, onDecrement, resetStepperTimeout]);

  const handlePress = useCallback(() => {
    // Close the stepper if the customer taps the same ingredient again.
    if (activeStepperId === ingredientModification.ingredient.id) {
      setActiveStepperId(null);

      return;
    }

    // Do not use the stepper for dressings, these use a checkmark instead.
    if (isDressing && quantity === 0) {
      onIncrement(ingredientModification);

      return;
    }

    // When the dressing checkmark is already check, uncheck it.
    if (isDressing && quantity !== 0) {
      onDecrement(ingredientModification);

      return;
    }

    // Automatically increment modification if it is not selected, triggering the limitations banner.
    if (quantity === 0) {
      onIncrement(ingredientModification);
    }

    // Do not open the stepper for new modifications if they can't be added.
    if (quantity === 0 && isAtMaxQuantity) return;

    setActiveStepperId(ingredientModification.ingredient.id);
    resetStepperTimeout();
  }, [
    activeStepperId,
    ingredientModification,
    isAtMaxQuantity,
    quantity,
    isDressing,
    setActiveStepperId,
    resetStepperTimeout,
    onIncrement,
    onDecrement,
  ]);

  const isStepperActive = useMemo(
    () => activeStepperId === ingredientModification.ingredient.id,
    [activeStepperId, ingredientModification.ingredient.id],
  );

  return (
    <IngredientCardV2.Pressable
      onPress={handlePress}
      accessibilityLabel={accessibilityLabel}
    >
      <IngredientCardV2.ImageContainer>
        <IngredientCardV2.Image
          source={ingredientModification.ingredient.asset.url}
          accessibilityLabel={ingredientModification.ingredient.name}
          imageWidth={INGREDIENT_IMAGE_WIDTH}
        />

        {isStepperActive ? (
          <IngredientCardV2.QuantityStepper.Container>
            <IngredientCardV2.QuantityStepper.StepperButton
              variation="decrease"
              onPress={handleDecrement}
              accessibilityLabel={formatMessage(messages.a11yDecrementLabel)}
            />

            <IngredientCardV2.QuantityStepper.Label quantity={quantity} />

            <IngredientCardV2.QuantityStepper.StepperButton
              variation="increase"
              onPress={handleIncrement}
              accessibilityLabel={formatMessage(messages.a11yIncrementLabel)}
            />
          </IngredientCardV2.QuantityStepper.Container>
        ) : null}

        {!isStepperActive && quantity > 0 ? (
          <IngredientCardV2.Quantity shouldUseIcon={isDressing}>
            {quantity}
          </IngredientCardV2.Quantity>
        ) : null}
      </IngredientCardV2.ImageContainer>

      <IngredientCardV2.Label>
        {ingredientModification.ingredient.name}
      </IngredientCardV2.Label>

      {ingredientModification.outOfStock ? (
        <IngredientCardV2.NoticeLabel>
          <FormattedMessage {...messages.unavailableLabel} />
        </IngredientCardV2.NoticeLabel>
      ) : null}

      {dietaryRestrictionNote && !ingredientModification.outOfStock ? (
        <IngredientCardV2.NoticeLabel>
          {dietaryRestrictionNote}
        </IngredientCardV2.NoticeLabel>
      ) : null}
    </IngredientCardV2.Pressable>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  a11yCardLabel: {
    defaultMessage: `{quantity, select,
      0 {Add {ingredient_name}}
      other {Increase quantity of {ingredient_name} (current quantity: {quantity})}
    }`,
    description: 'Customization | Ingredient card | a11y label',
  },
  a11yIncrementLabel: {
    defaultMessage: 'Increase product quantity',
    description:
      'Customization | Ingredient card increment button | a11y label',
  },
  a11yDecrementLabel: {
    defaultMessage: 'Decrease product quantity',
    description:
      'Customization | Ingredient card decrement button | a11y label',
  },
  unavailableLabel: {
    defaultMessage: 'Unavailable',
    description: 'Customization | Ingredient card | Unavailable label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type ProductDetailsIngredientCardProps = {
  dietaryRestrictionNote: string;
  ingredientModification: IngredientModification;
  quantity: number;
  palette?: 'kale' | 'spinach';
  activeStepperId: string | null;
  isAtMaxQuantity: boolean;
  onIncrement: (ingredientModification: IngredientModification) => void;
  onDecrement: (ingredientModification: IngredientModification) => void;
  setActiveStepperId: (id: string | null) => void;
  resetStepperTimeout: () => void;
};
